import { UnitData } from "../Units/UnitData";
import { Utilities } from "../../../../utils/utilities";
import { PropertyData } from "../PropertyData";
import { collection, doc, getDocs, getFirestore, query, setDoc, where } from "firebase/firestore";
import { LocalData } from "../../General/LocalData";
import { FirebaseEvents } from "../../../../utils/firebaseEvents";

export class IncomeOverviewData {
  property: PropertyData;
  yearDatas: IncomeOverviewYearData[] = [];

  constructor(property: PropertyData) {
    this.property = property;
  }

  getDataForYear(year: number): IncomeOverviewYearData {
    for (let data of this.yearDatas) {
      if (data.year === year) {
        return data;
      }
    }
    let data = new IncomeOverviewYearData(this, year);
    this.yearDatas.push(data);
    return data;
  }
}

export class IncomeOverviewYearData {
  overview: IncomeOverviewData;
  year: number;
  unitDatas: IncomeOverviewUnitData[] = [];
  loaded: boolean = false;

  constructor(overview: IncomeOverviewData, year: number) {
    this.overview = overview;
    this.year = year;
  }

  getUnitData(unit: UnitData): IncomeOverviewUnitData {
    for (let data of this.unitDatas) {
      if (data.unit.id === unit.id) {
        return data;
      }
    }
    const data = new IncomeOverviewUnitData(unit, this);
    this.unitDatas.push(data);
    return data;
  }

  load(completion: (error?: Error) => void) {
    const db = getFirestore();
    const incomeOverviewRef = collection(db, "properties", this.overview.property.id, "incomeOverview");
    const filteredQuery = query(incomeOverviewRef, where("year", "==", this.year));

    getDocs(filteredQuery)
      .then((snapshot) => {
        console.log("Loaded income");
        this.loaded = true;
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data) {
            const unitID = data["unitID"];
            if (unitID) {
              const unitData = this.getUnitData(LocalData.getUnitWithId(unitID));
              const datas = data["datas"] as {
                [key: string]: {
                  sum: number;
                  userID: string;
                  updateDate: any;
                  paidDate: any;
                }[];
              };
              Object.keys(datas).forEach((month) => {
                const monthDatas = datas[month];
                if (monthDatas.length > 0) {
                  unitData.getDataForMonth(parseInt(month)).setup(monthDatas[0]);
                }
              });
            }
          }
        });

        completion(undefined);
      })
      .catch((error) => {
        console.log("Error loading income");
        console.log(error);
        this.loaded = true;
        completion(error);
      });
  }
}

class IncomeOverviewUnitData {
  unit: UnitData;
  yearData: IncomeOverviewYearData;
  datas: IncomeOverviewUnitMonthData[] = [];

  constructor(unit: UnitData, yearData: IncomeOverviewYearData) {
    this.unit = unit;
    this.yearData = yearData;
  }

  save(completion: (error?: Error) => void) {
    let updateData: { [key: string]: any } = {
      year: this.yearData.year,
      unitID: this.unit.id,
      datas: {},
    };
    this.datas.forEach((monthData) => {
      const dataToSave = monthData.toMap();
      if (Object.keys(dataToSave).length > 0) {
        updateData["datas"][monthData.month] = [dataToSave];
      }
    });

    const db = getFirestore();
    const incomeOverviewDocRef = doc(db, "properties", this.unit.property?.id ?? "", "incomeOverview", `${this.yearData.year}_${this.unit.id}`);

    setDoc(incomeOverviewDocRef, updateData, { merge: true })
      .then(() => {
        console.log("Saved income");
        completion(undefined);
        FirebaseEvents.logEvent("income_overview_submitted", {});
      })
      .catch((error) => {
        console.log("Error saving income");
        console.log(error);
        completion(error);
      });
  }

  getDataForMonth(month: number): IncomeOverviewUnitMonthData {
    for (let data of this.datas) {
      if (data.month === month) {
        return data;
      }
    }
    const data = new IncomeOverviewUnitMonthData(month);
    this.datas.push(data);
    return data;
  }
}

class IncomeOverviewUnitMonthData {
  month: number;

  userID: string = "";
  sum: number = 0;
  updateDate?: Date;
  paidDate?: Date;

  constructor(month: number) {
    this.month = month;
  }

  setup(data: { [key: string]: any }) {
    if (Utilities.isValidNumber(data.sum)) {
      this.sum = data.sum;
    }
    if (Utilities.isValidString(data.userID)) {
      this.userID = data.userID;
    }
    const updateDate = Utilities.getDateFromTimestamp(data.updateDate);
    if (updateDate) {
      this.updateDate = updateDate;
    }
    const paidDate = Utilities.getDateFromTimestamp(data.paidDate);
    if (paidDate) {
      this.paidDate = paidDate;
    }
  }

  toMap(): { [key: string]: any } {
    const data: { [key: string]: any } = {};
    if (this.userID) {
      data["userID"] = this.userID;
      data["sum"] = this.sum;
    }
    if (this.updateDate) {
      data["updateDate"] = this.updateDate;
    }
    if (this.paidDate) {
      data["paidDate"] = this.paidDate;
    }
    return data;
  }
}
